import { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, Textarea, Alert,
    CircularProgress, Divider, Select, Option, IconButton,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon      from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon            from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon         from '@mui/icons-material/RemoveRounded';
import EditRoundedIcon           from '@mui/icons-material/EditRounded';
import CheckCircleRoundedIcon    from '@mui/icons-material/CheckCircleRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import CancelRoundedIcon         from '@mui/icons-material/CancelRounded';
import SendRoundedIcon           from '@mui/icons-material/SendRounded';
import InfoOutlinedIcon          from '@mui/icons-material/InfoOutlined';
import SwapHorizRoundedIcon      from '@mui/icons-material/SwapHorizRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetMyAvailability,
    APICreateChangeRequest,
    APICreateChangeRequestsBatch,
    APIGetMyChangeRequests,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const moscowToday = () => {
    const date = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Moscow' });
    return new Date(`${date}T00:00:00`);
};

const MONTHS_RU = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const DAYS_FULL_RU = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];

const fmtDateFull = (dateStr) => {
    const d = new Date(dateStr);
    return `${DAYS_FULL_RU[d.getDay()]}, ${d.getDate()} ${MONTHS_RU[d.getMonth()]}`;
};

const CHANGE_TYPE_META = {
    add:    { label: 'Добавить день',    icon: <AddRoundedIcon sx={{ fontSize: 15 }} />,          color: 'success' },
    remove: { label: 'Убрать день',      icon: <RemoveRoundedIcon sx={{ fontSize: 15 }} />,       color: 'danger' },
    swap:   { label: 'Заменить день',  icon: <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'currentColor', display: 'inline-block' }} />, color: 'primary' },
    edit:   { label: 'Изменить время',   icon: <EditRoundedIcon sx={{ fontSize: 15 }} />,         color: 'primary' },
};

const STATUS_META = {
    pending:  { label: 'На рассмотрении', icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 14 }} />, color: 'warning' },
    approved: { label: 'Одобрен',         icon: <CheckCircleRoundedIcon    sx={{ fontSize: 14 }} />, color: 'success' },
    rejected: { label: 'Отклонён',        icon: <CancelRoundedIcon         sx={{ fontSize: 14 }} />, color: 'danger'  },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, x: 32 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, x: 32, transition: { duration: 0.22 } },
};

const cardVariants = {
    hidden:  { opacity: 0, y: 16 },
    visible: (i) => ({
        opacity: 1, y: 0,
        transition: { delay: i * 0.07, duration: 0.32, ease: [0.22, 1, 0.36, 1] },
    }),
};

// ─── Компонент: карточка истории запроса ─────────────────────────────────────
function RequestHistoryCard({ req, index }) {
    const status = STATUS_META[req.status]  || STATUS_META.pending;
    const type   = CHANGE_TYPE_META[req.change_type] || CHANGE_TYPE_META.edit;

    return (
        <motion.div custom={index} variants={cardVariants} initial="hidden" animate="visible">
            <Box sx={{
                px:           2,
                py:           1.5,
                borderRadius: 'var(--joy-radius-lg)',
                border:       '1px solid var(--joy-palette-divider)',
                bgcolor:      'var(--joy-palette-background-level1)',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                            {fmtDateFull(req.date)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            {type.label}
                            {req.change_type === 'swap' && req.swap_to_date && ` → ${fmtDateFull(req.swap_to_date)}`}
                            {(req.change_type === 'edit' || req.change_type === 'add' || req.change_type === 'swap')
                                && req.new_time && (
                                    <Box component="span" sx={{ ml: 0.5, color: '#22c55e', fontWeight: 700 }}>
                                        {req.change_type === 'edit' ? '→' : 'с'} {req.new_time.slice(0, 5)}
                                    </Box>
                                )
                            }
                        </Typography>
                    </Box>

                    <Chip
                        size="sm"
                        color={status.color}
                        variant="soft"
                        startDecorator={status.icon}
                        sx={{ fontWeight: 600, fontSize: 11 }}
                    >
                        {status.label}
                    </Chip>
                </Box>

                {req.comment && (
                    <Typography sx={{
                        fontSize: 12, color: 'var(--joy-palette-text-tertiary)',
                        mt: 0.8, fontStyle: 'italic',
                    }}>
                        «{req.comment}»
                    </Typography>
                )}

                {req.review_comment && (
                    <Box sx={{
                        mt: 1, px: 1.5, py: 0.8,
                        borderRadius: 'var(--joy-radius-sm)',
                        bgcolor: req.status === 'approved'
                            ? 'rgba(34,197,94,0.08)'
                            : 'rgba(248,113,113,0.08)',
                        borderLeft: `3px solid ${req.status === 'approved'
                            ? 'var(--joy-palette-success-500)'
                            : 'var(--joy-palette-danger-500)'}`,
                    }}>
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)' }}>
                            Ответ руководителя: {req.review_comment}
                        </Typography>
                    </Box>
                )}
            </Box>
        </motion.div>
    );
}

// ════════════════════════════════════════════════════════════════════
//  ChangeRequestPage
// ════════════════════════════════════════════════════════════════════
export default function ChangeRequestPage() {
    const { token, showToast } = useContext(AppContext);
    const navigate = useNavigate();

    // ── Стейт формы ───────────────────────────────────────────────
    const [selectedDates, setSelectedDates] = useState([]);   // массив 'YYYY-MM-DD'
    const [swapToDate, setSwapToDate] = useState('');
    const [changeType,   setChangeType]   = useState('');
    const [newTime,      setNewTime]      = useState('');
    const [comment,      setComment]      = useState('');
    const [submitting,   setSubmitting]   = useState(false);
    const [submitError,  setSubmitError]  = useState('');
    const [fieldErrors,  setFieldErrors]  = useState({});
    const [submitOk,     setSubmitOk]     = useState(false);

    // ── Данные ────────────────────────────────────────────────────
    const [availability, setAvailability] = useState([]);
    const [history,      setHistory]      = useState([]);
    const [loadHistory,  setLoadHistory]  = useState(true);

    const loadData = useCallback(async () => {
        setLoadHistory(true);

        // Текущая неделя: понедельник
        const now = moscowToday();
        const dow = (now.getDay() + 6) % 7;
        const thisMonday = new Date(now);
        thisMonday.setDate(now.getDate() - dow);
        const nextMonday = new Date(thisMonday);
        nextMonday.setDate(thisMonday.getDate() + 7);

        const [availThis, availNext, hist] = await Promise.all([
            APIGetMyAvailability(token, toISO(thisMonday)),
            APIGetMyAvailability(token, toISO(nextMonday)),
            APIGetMyChangeRequests(token),
        ]);

        // API возвращает { days: [...] } или [...] — обрабатываем оба формата
        const daysThis = Array.isArray(availThis) ? availThis : (availThis?.days || []);
        const daysNext = Array.isArray(availNext) ? availNext : (availNext?.days || []);
        setAvailability([...daysThis, ...daysNext]);

        if (Array.isArray(hist)) setHistory(hist);
        setLoadHistory(false);
    }, [token]);

    useEffect(() => { loadData(); }, [loadData]);

    // ── Доступность ───────────────────────────────────────────────
    const availByDate = useMemo(() => {
        const m = {};
        availability.forEach(a => { m[a.date] = a; });
        return m;
    }, [availability]);

    // Понедельник следующей недели как старт
    const toISO = (d) => {
        const x = new Date(d); x.setHours(0,0,0,0);
        return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`;
    };

    // Текущая + следующая неделя
    const buildWeek = (mondayOffsetWeeks) => {
        const now = moscowToday();
        const dow = (now.getDay() + 6) % 7;                       // 0=Пн … 6=Вс
        const monday = new Date(now);
        monday.setDate(now.getDate() - dow + 7 * mondayOffsetWeeks); // 0 = текущая неделя
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(monday); d.setDate(monday.getDate() + i);
            return toISO(d);
        });
    };

    const weeks = useMemo(() => [buildWeek(0), buildWeek(1)], []); // текущая + следующая

    const WEEKDAYS_SHORT = ['ПН','ВТ','СР','ЧТ','ПТ','СБ','ВС'];
    const isWorkingDay = (iso) => !!availByDate[iso];        // есть в графике
    const isMultiAllowed = !!changeType;

    // Даты, на которые уже есть pending-запрос
    const pendingDates = useMemo(() => {
        const s = new Set();
        history.forEach(r => {
            if (r.status === 'pending') s.add(r.date);
        });
        return s;
    }, [history]);

    // Переключение дня в выборе
    const toggleDay = (iso) => {
        setSubmitError('');
        const working = isWorkingDay(iso);

        // Защита: уже есть запрос на рассмотрении
        if (pendingDates.has(iso)) {
            setSubmitError('На этот день уже есть запрос на рассмотрении');
            return;
        }

        // Защита: «убрать день» / «изменить время» / «заменить» — только для рабочих дней
        if ((changeType === 'remove' || changeType === 'edit' || changeType === 'swap') && !working) {
            setSubmitError('Этот день и так выходной — нечего убирать');
            return;
        }
        // Защита: «добавить день» — только для выходных
        if (changeType === 'add' && working) {
            setSubmitError('Ты уже работаешь в этот день');
            return;
        }

        setSelectedDates(prev => {
            if (changeType === 'swap') {
                return prev.includes(iso) ? [] : [iso];
            }
            return prev.includes(iso) ? prev.filter(x => x !== iso) : [...prev, iso];
        });
    };

    // ── Отправка ─────────────────────────────────────────────────
    const handleSubmit = async () => {
        const errs = {};
        if (selectedDates.length === 0)        errs.date        = 'Выбери хотя бы один день';
        if (!changeType)                       errs.change_type = 'Выбери тип изменения';
        if (changeType === 'swap' && !swapToDate) errs.date      = 'Выбери день, на который меняем';
        if (!comment.trim())                   errs.comment     = 'Напиши комментарий';

        setFieldErrors(errs);
        if (Object.keys(errs).length) {
            setSubmitError('Заполни обязательные поля');
            return;
        }

        setSubmitError('');
        setSubmitting(true);

        const res = await APICreateChangeRequestsBatch(token, {
            dates:        selectedDates,
            change_type:  changeType,
            new_time:     (changeType === 'edit' || changeType === 'add' || changeType === 'swap') && newTime ? newTime : null,
            swap_to_date: changeType === 'swap' ? swapToDate : null,
            comment:      comment.trim(),
        });

        if (!res?.error) {
            setSubmitOk(true);
            setFieldErrors({});
            showToast(
                selectedDates.length > 1
                    ? `Отправлено запросов: ${selectedDates.length} ✓`
                    : 'Запрос отправлен руководителю ✓'
            );
            setSelectedDates([]); setChangeType(''); setNewTime(''); setSwapToDate(''); setComment('');
            await loadData();
            setTimeout(() => setSubmitOk(false), 3000);
        } else {
            setSubmitError(parseApiError(res.error, 'Ошибка отправки запроса'));
        }
        setSubmitting(false);
    };

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Заголовок ──────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.05 }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <Box
                            onClick={() => navigate('/')}
                            sx={{
                                display:      'flex',
                                alignItems:   'center',
                                justifyContent: 'center',
                                width:        36,
                                height:       36,
                                borderRadius: 'var(--joy-radius-md)',
                                border:       '1px solid var(--joy-palette-divider)',
                                bgcolor:      'var(--joy-palette-background-level1)',
                                cursor:       'pointer',
                                transition:   'all .15s',
                                '&:hover': {
                                    borderColor: 'var(--joy-palette-primary-500)',
                                    bgcolor:     'rgba(162,38,226,0.08)',
                                },
                            }}
                        >
                            <ArrowBackRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-secondary)' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                Запрос на изменение
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                После дедлайна — через руководителя
                            </Typography>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Инфо-баннер ────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.32, delay: 0.1 }}
                >
                    <Box sx={{
                        display:      'flex',
                        gap:          1.2,
                        px:           2,
                        py:           1.4,
                        mb:           2.5,
                        borderRadius: 'var(--joy-radius-lg)',
                        bgcolor:      'rgba(162,38,226,0.07)',
                        border:       '1px solid rgba(162,38,226,0.2)',
                    }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-primary-400)', mt: '1px', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', lineHeight: 1.55 }}>
                            Дедлайн заполнения прошёл. Запрос будет отправлен руководителю на рассмотрение.
                            Ты получишь уведомление с ответом.
                        </Typography>
                    </Box>
                </motion.div>

                {/* ── Форма ─────────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.38, delay: 0.14 }}
                >
                    <Box sx={{
                        bgcolor:      'var(--joy-palette-background-level1)',
                        border:       '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        overflow:     'hidden',
                        mb:           2.5,
                    }}>
                        <Box sx={{
                            px:           { xs: 2, sm: 2.5 },
                            py:           1.8,
                            borderBottom: '1px solid var(--joy-palette-divider)',
                        }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                Новый запрос
                            </Typography>
                        </Box>

                        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
                            <Stack spacing={2.5}>

                                {/* Тип изменения */}
                                <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--joy-palette-text-secondary)', mb: 1 }}>
                                        Тип изменения
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {Object.entries(CHANGE_TYPE_META).map(([key, meta]) => (
                                            <Box
                                                key={key}
                                                onClick={() => {
                                                    setChangeType(key);
                                                    setSubmitError('');
                                                    setSelectedDates([]);
                                                    setFieldErrors({});
                                                    if (key !== 'edit' && key !== 'add' && key !== 'swap') setNewTime('');
                                                    setSwapToDate('');
                                                }}
                                                sx={{
                                                    display:      'flex',
                                                    alignItems:   'center',
                                                    gap:          0.7,
                                                    px:           1.5,
                                                    py:           0.7,
                                                    borderRadius: 'var(--joy-radius-md)',
                                                    border:       '1.5px solid',
                                                    borderColor:  changeType === key
                                                        ? 'var(--joy-palette-primary-500)'
                                                        : 'var(--joy-palette-divider)',
                                                    bgcolor:      changeType === key
                                                        ? 'rgba(162,38,226,0.1)'
                                                        : 'var(--joy-palette-background-level2)',
                                                    cursor:       'pointer',
                                                    transition:   'all .16s',
                                                    '&:hover':    { borderColor: 'var(--joy-palette-primary-400)' },
                                                }}
                                            >
                                                <Box sx={{
                                                    color: changeType === key
                                                        ? 'var(--joy-palette-primary-400)'
                                                        : 'var(--joy-palette-text-secondary)',
                                                    display: 'flex',
                                                }}>
                                                    {meta.icon}
                                                </Box>
                                                <Typography sx={{
                                                    fontSize:   12,
                                                    fontWeight: changeType === key ? 700 : 500,
                                                    color: changeType === key
                                                        ? 'var(--joy-palette-primary-300)'
                                                        : 'var(--joy-palette-text-primary)',
                                                }}>
                                                    {meta.label}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>

                                {/* Выбор даты(дат) */}
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--joy-palette-text-secondary)' }}>
                                            <span key={changeType || 'empty'}>{changeType === 'add'    ? 'Выберите дни для добавления'
                                            : changeType === 'remove' ? 'Выберите дни для удаления'
                                            : changeType === 'edit'   ? 'Выберите дни для смены времени'
                                            : changeType === 'swap'   ? 'Какой день заменить (откуда)'
                                            : 'Дни'}</span>
                                        </Typography>
                                        {selectedDates.length > 0 && (
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-primary-300)', fontWeight: 700 }}>
                                                выбрано: {selectedDates.length}
                                            </Typography>
                                        )}
                                    </Box>

                                    {!changeType ? (
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', py: 1 }}>
                                            Сначала выбери тип изменения выше
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1.6}>
                                            {weeks.map((week, wi) => {
                                                const todayISO = toISO(moscowToday());
                                                return (
                                                    <Box key={wi}>
                                                        <Typography sx={{
                                                            fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                                                            textTransform: 'uppercase', color: 'var(--joy-palette-text-tertiary)',
                                                            mb: 0.8,
                                                        }}>
                                                            {wi === 0 ? 'Эта неделя' : 'Следующая неделя'}
                                                        </Typography>
                                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.8 }}>
                                                            {week.map(iso => {
                                                                const d        = new Date(iso);
                                                                const dow      = (d.getDay() + 6) % 7;     // 0=Пн … 6=Вс
                                                                const working  = isWorkingDay(iso);        // есть в графике
                                                                const selected = selectedDates.includes(iso);
                                                                const isToday  = iso === todayISO;
                                                                const isPast       = iso < todayISO;
                                                                const hasPending   = pendingDates.has(iso);
                                                                const isDisabled   = loadHistory || isPast || hasPending || (
                                                                    (changeType === 'remove' || changeType === 'edit' || changeType === 'swap') && !working
                                                                ) || (changeType === 'add' && working);

                                                                // Цвет акцента: рабочий день — зелёный, выходной — фиолетовый
                                                                const accent     = working ? '#22c55e' : 'var(--joy-palette-primary-400)';
                                                                const accentSoft = working ? 'rgba(34,197,94,0.5)' : 'rgba(162,38,226,0.5)';
                                                                const accentBg   = working ? 'rgba(34,197,94,0.1)' : 'rgba(162,38,226,0.1)';

                                                                return (
                                                                    <Box
                                                                        key={iso}
                                                                        onClick={() => !isDisabled && toggleDay(iso)}
                                                                        sx={{
                                                                            textAlign: 'center',
                                                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                                            opacity: isDisabled ? 0.35 : 1,
                                                                        }}
                                                                    >
                                                                        <Typography sx={{
                                                                            fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                                                                            textTransform: 'uppercase',
                                                                            color: isToday ? 'var(--joy-palette-primary-400)' : 'var(--joy-palette-text-tertiary)',
                                                                        }}>
                                                                            {['ПН','ВТ','СР','ЧТ','ПТ','СБ','ВС'][dow]}
                                                                        </Typography>
                                                                        <Box sx={{
                                                                            mt: 0.5, mx: 'auto',
                                                                            width: { xs: 30, sm: 38 }, height: { xs: 30, sm: 38 }, borderRadius: 'var(--joy-radius-md)',
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            border: '2px solid',
                                                                            borderColor: selected ? accent : accentSoft,
                                                                            bgcolor: selected ? accentBg : (working ? 'rgba(34,197,94,0.04)' : 'transparent'),
                                                                            boxShadow: selected ? `0 0 0 3px ${working ? 'rgba(34,197,94,0.18)' : 'rgba(162,38,226,0.18)'}` : 'none',
                                                                            transition: 'all .15s',
                                                                            '&:hover': isDisabled ? {} : { borderColor: accent },
                                                                        }}>
                                                                            <Typography sx={{
                                                                                fontSize: 15, fontWeight: 800,
                                                                                color: selected ? accent : (working ? '#22c55e' : 'var(--joy-palette-text-secondary)'),
                                                                            }}>
                                                                                {d.getDate()}
                                                                            </Typography>
                                                                        </Box>
                                                                        {/* галочка/точка статуса под плиткой */}
                                                                        <Typography sx={{
                                                                            fontSize: 10, mt: 0.3, fontWeight: 700,
                                                                            color: hasPending ? '#f59e0b' : (selected ? accent : (working ? '#22c55e' : 'transparent')),
                                                                            minHeight: 14,
                                                                        }}>
                                                                            <Box component="span" key={`${hasPending}:${selected}:${changeType}:${working}`}>
                                                                                {hasPending ? <span>⏳</span> : selected
                                                                                    ? <span>{changeType === 'add' ? <AddRoundedIcon sx={{ fontSize: 14 }} />
                                                                                        : changeType === 'remove' ? <RemoveRoundedIcon sx={{ fontSize: 14 }} />
                                                                                        : changeType === 'edit' ? <EditRoundedIcon sx={{ fontSize: 14 }} /> : '●'}</span>
                                                                                    : <span>{working ? '✓' : ''}</span>}
                                                                            </Box>
                                                                        </Typography>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}

                                            {/* Легенда */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box sx={{ width: 9, height: 9, borderRadius: 'var(--joy-radius-sm)', border: '2px solid rgba(34,197,94,0.5)', bgcolor: 'rgba(34,197,94,0.1)' }} />
                                                    <Typography sx={{ fontSize: 10.5, color: 'var(--joy-palette-text-tertiary)' }}>работаю</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box sx={{ width: 9, height: 9, borderRadius: 'var(--joy-radius-sm)', border: '2px solid rgba(162,38,226,0.5)' }} />
                                                    <Typography sx={{ fontSize: 10.5, color: 'var(--joy-palette-text-tertiary)' }}>выходной</Typography>
                                                </Box>
                                            </Box>

                                            {fieldErrors.date && (
                                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'rgba(244,67,54,0.95)' }}>
                                                    {fieldErrors.date}
                                                </Typography>
                                            )}
                                        </Stack>
                                    )}
                                </Box>

                                {/* Целевой день — только для swap */}
                                {changeType === 'swap' && selectedDates.length === 1 && (
                                    <Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--joy-palette-text-secondary)', mb: 1 }}>
                                                На какой день заменить
                                            </Typography>
                                            <Stack spacing={1.6}>
                                                {weeks.map((week, wi) => {
                                                    const todayISO = toISO(moscowToday());
                                                    return (
                                                        <Box key={wi}>
                                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.8 }}>
                                                                {week.map(iso => {
                                                                    const d        = new Date(iso);
                                                                    const dow      = (d.getDay() + 6) % 7;
                                                                    const working  = isWorkingDay(iso);
                                                                    const selected = swapToDate === iso;
                                                                    const isSource = selectedDates[0] === iso;
                                                                    const isPast   = iso < todayISO;
                                                                    // целевой день логично выбирать из НЕрабочих
                                                                    const accent     = '#06b6d4';
                                                                    const disabled   = loadHistory || isPast || isSource || working || pendingDates.has(iso);
                                                                    return (
                                                                        <Box
                                                                            key={iso}
                                                                            onClick={() => { if (!disabled) { setSwapToDate(iso); setSubmitError(''); } }}
                                                                            sx={{
                                                                                textAlign: 'center',
                                                                                cursor: disabled ? 'not-allowed' : 'pointer',
                                                                                opacity: disabled ? 0.35 : 1,
                                                                            }}
                                                                        >
                                                                            <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--joy-palette-text-tertiary)' }}>
                                                                                {['ПН','ВТ','СР','ЧТ','ПТ','СБ','ВС'][dow]}
                                                                            </Typography>
                                                                            <Box sx={{
                                                                                mt: 0.5, mx: 'auto',
                                                                                width: 38, height: 38, borderRadius: 'var(--joy-radius-md)',
                                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                border: '2px solid',
                                                                                borderColor: selected ? accent : (working ? 'rgba(34,197,94,0.4)' : 'var(--joy-palette-divider)'),
                                                                                bgcolor: selected ? 'rgba(6,182,212,0.16)' : 'transparent',
                                                                                boxShadow: selected ? '0 0 0 3px rgba(6,182,212,0.18)' : 'none',
                                                                                transition: 'all .15s',
                                                                            }}>
                                                                                <Typography sx={{ fontSize: 15, fontWeight: 800, color: selected ? accent : (working ? '#22c55e' : 'var(--joy-palette-text-secondary)') }}>
                                                                                    {d.getDate()}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Typography sx={{ fontSize: 9, mt: 0.3, fontWeight: 700, color: isSource ? 'var(--joy-palette-text-tertiary)' : 'transparent', minHeight: 12 }}>
                                                                                {isSource ? 'откуда' : ''}
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>

                                            {/* Наглядная сводка swap */}
                                            {swapToDate && (
                                                <Box sx={{
                                                    mt: 1.5, px: 2, py: 1.2,
                                                    borderRadius: 'var(--joy-radius-md)',
                                                    bgcolor: 'rgba(6,182,212,0.08)',
                                                    border: '1px solid rgba(6,182,212,0.3)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                                                }}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                        {fmtDateFull(selectedDates[0])}
                                                    </Typography>
                                                    <SwapHorizRoundedIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#06b6d4' }}>
                                                        {fmtDateFull(swapToDate)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {/* Новое время (только для "edit") */}
                                {(changeType === 'edit' || changeType === 'add' || changeType === 'swap') && (
                                    <Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--joy-palette-text-secondary)', mb: 1 }}>
                                                {changeType === 'edit'
                                                    ? 'Новое время выхода (для всех выбранных дней)'
                                                    : 'Время выхода'}
                                                {changeType === 'add'
                                                    ? <Box component="span" sx={{ color: 'var(--joy-palette-text-tertiary)', ml: 0.5, fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(необязательно)</Box>
                                                    : <Box component="span" sx={{ color: '#f87171', ml: 0.5 }}>*</Box>}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Box
                                                component="input"
                                                type="time"
                                                value={newTime}
                                                onChange={e => { setNewTime(e.target.value); setFieldErrors(p => ({ ...p, new_time: undefined })); }}
                                                sx={{
                                                    flex:         1,
                                                    minWidth:     0,
                                                    bgcolor:      'var(--joy-palette-background-level2)',
                                                    border:       '1px solid',
                                                    borderColor:  fieldErrors.new_time
                                                        ? 'rgba(244,67,54,0.7)'
                                                        : 'var(--joy-palette-divider)',
                                                    boxShadow:    fieldErrors.new_time ? '0 0 0 3px rgba(244,67,54,0.12)' : 'none',
                                                    borderRadius: 'var(--joy-radius-md)',
                                                    color:        'var(--joy-palette-text-primary)',
                                                    fontSize:     14,
                                                    fontWeight:   600,
                                                    px:           1.5,
                                                    py:           1,
                                                    outline:      'none',
                                                    boxSizing:    'border-box',
                                                    '&:focus': {
                                                        borderColor: fieldErrors.new_time
                                                            ? 'rgba(244,67,54,1)'
                                                            : 'var(--joy-palette-primary-500)',
                                                    },
                                                }}
                                            />
                                            {newTime && (
                                                <IconButton
                                                    size="sm" variant="soft" color="neutral"
                                                    onClick={() => { setNewTime(''); setFieldErrors(p => ({ ...p, new_time: undefined })); }}
                                                    sx={{ flexShrink: 0, borderRadius: 'var(--joy-radius-md)' }}
                                                    aria-label="Очистить время"
                                                >
                                                    ✕
                                                </IconButton>
                                            )}
                                            </Box>
                                            {fieldErrors.new_time && (
                                                <Typography sx={{ mt: 0.5, fontSize: 11, fontWeight: 600, color: 'rgba(244,67,54,0.95)' }}>
                                                    {fieldErrors.new_time}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {/* Комментарий */}
                                <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--joy-palette-text-secondary)', mb: 1 }}>
                                        Комментарий
                                        <Box component="span" sx={{ fontWeight: 400, ml: 0.5, textTransform: 'none', letterSpacing: 0, color: '#ef4444' }}>
                                            (обязательно)
                                        </Box>
                                    </Typography>
                                    <Textarea
                                        value={comment}
                                        onChange={e => { setComment(e.target.value); setFieldErrors(p => ({ ...p, comment: undefined })); }}
                                        placeholder="Объясни причину изменения…"
                                        minRows={3}
                                        sx={{
                                            bgcolor: 'var(--joy-palette-background-level2)',
                                            border:  '1px solid',
                                            borderColor: fieldErrors.comment
                                                ? 'rgba(244,67,54,0.7)'
                                                : 'var(--joy-palette-divider)',
                                            boxShadow: fieldErrors.comment ? '0 0 0 3px rgba(244,67,54,0.12)' : 'none',
                                            fontSize: 13, color: 'white',
                                            '&:focus-within': {
                                                borderColor: fieldErrors.comment
                                                    ? 'rgba(244,67,54,1)'
                                                    : 'var(--joy-palette-primary-500)',
                                            },
                                        }}
                                    />
                                    {fieldErrors.comment && (
                                        <Typography sx={{ mt: 0.5, fontSize: 11, fontWeight: 600, color: 'rgba(244,67,54,0.95)' }}>
                                            {fieldErrors.comment}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Ошибка */}
                                <AnimatePresence>
                                    {submitError && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                        >
                                            <Alert color="danger" variant="soft" size="sm" sx={{ borderRadius: 'var(--joy-radius-md)', fontSize: 13 }}>
                                                {submitError}
                                            </Alert>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Кнопка отправки */}
                                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                    <Button
                                        fullWidth
                                        loading={submitting}
                                        onClick={handleSubmit}
                                        startDecorator={submitOk
                                            ? <CheckCircleRoundedIcon />
                                            : <SendRoundedIcon sx={{ fontSize: 15 }} />
                                        }
                                        sx={{
                                            py:         1.4,
                                            borderRadius: 'var(--joy-radius-md)',
                                            background: submitOk
                                                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                                                : 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                            color:      '#fff',
                                            fontWeight: 700,
                                            fontSize:   14,
                                            border:     'none',
                                            boxShadow:  submitOk
                                                ? '0 4px 16px rgba(34,197,94,0.35)'
                                                : '0 4px 16px rgba(162,38,226,0.35)',
                                            transition: 'all .25s',
                                            '&:hover':  {
                                                boxShadow: '0 8px 24px rgba(162,38,226,0.5)',
                                            },
                                        }}
                                    >
                                        {submitOk ? 'Запрос отправлен!' : 'Отправить запрос'}
                                    </Button>
                                </motion.div>

                            </Stack>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── История запросов ──────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.38, delay: 0.22 }}
                >
                    <Box sx={{
                        bgcolor:      'var(--joy-palette-background-level1)',
                        border:       '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        overflow:     'hidden',
                    }}>
                        <Box sx={{
                            px:           { xs: 2, sm: 2.5 },
                            py:           1.8,
                            borderBottom: '1px solid var(--joy-palette-divider)',
                            display:      'flex',
                            alignItems:   'center',
                            justifyContent: 'space-between',
                        }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                История запросов
                            </Typography>
                            {history.length > 0 && (
                                <Chip size="sm" sx={{
                                    bgcolor:  'rgba(162,38,226,0.1)',
                                    color:    'var(--joy-palette-primary-300)',
                                    fontSize: 11, fontWeight: 700,
                                }}>
                                    {history.length}
                                </Chip>
                            )}
                        </Box>

                        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                            {loadHistory ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <MemeLoader />
                                </Box>
                            ) : history.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                        Запросов пока не было
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1}>
                                    {history.map((req, i) => (
                                        <RequestHistoryCard key={req.id} req={req} index={i} />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Box>
                </motion.div>

            </Box>
        </motion.div>
    );
}